import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { CollabSessionErrorEvent } from '@common/sockets/types/collab-session-error-event.type';
import { TopicProperties } from '@portal-core/file-properties/models/topic-properties.model';
import { TopicPropertiesService } from '@portal-core/file-properties/services/topic-properties.service';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { ProjectFileFilterType } from '@portal-core/project-files/enums/project-files-filter-type.enum';
import { ProjectFilesService } from '@portal-core/project-files/services/project-files.service';
import { InputObservable } from '@portal-core/util/input-observable.decorator';
import { LoadingState } from '@portal-core/util/loading-state';
import { map, Observable, Subject, takeUntil, tap } from 'rxjs';

/** Top level component for displaying and editing topic properties. */
@Component({
  selector: 'mc-flare-properties-editor',
  templateUrl: './flare-properties-editor.component.html',
  styleUrls: ['./flare-properties-editor.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlarePropertiesEditorComponent implements OnInit, OnDestroy {

  // file properties inputs
  @Input() docCreatedAt: ISO8601DateString;
  @Input() docCommittedAt: ISO8601DateString;
  @Input() docModifiedAt: ISO8601DateString;
  @Input() docSize: number;

  // topic properties inputs
  @Input() filePath: string;
  @Input() projectId: number;
  @Input() branchName: string;
  @Input() commitId: string;
  @Input() readonly: boolean;
  @Input() usingWorkspaceTree: boolean;

  // collab session status bar inputs
  @Input() showStatusBar: boolean = true;
  @Input() collabSessionError: CollabSessionErrorEvent;
  @Input() docExceedsMaxSize: boolean;
  @Input() docExceedsMongoDBMaxSize: boolean;
  @Input() licenseUser: LicenseUser;
  @Input() offline: boolean;
  @Input() serverReadonly: boolean;

  @InputObservable('filePath') filePath$: Observable<string>;
  @InputObservable(['docCreatedAt', 'docCommittedAt', 'docModifiedAt']) dates$: Observable<[ISO8601DateString, ISO8601DateString, ISO8601DateString]>;

  /** Observable that emits whether the file is a topic or other file type. */
  isTopic$: Observable<boolean>;
  /** Observable that emits the latest topic properties whenever they change. */
  topicProperties$: Observable<TopicProperties>;
  /** The loading state of the topic properties, to avoid flickering. */
  loadingState: LoadingState<string> = new LoadingState<string>();

  private destroy$ = new Subject<void>();

  constructor(
    private projectFilesService: ProjectFilesService,
    private topicPropertiesService: TopicPropertiesService,
  ) { }

  ngOnInit() {
    this.topicProperties$ = this.topicPropertiesService.topicProperties$;

    // determine if the file is a topic or other type to know whether to load specific topic properties
    this.isTopic$ = this.filePath$.pipe(
      map(path => this.projectFilesService.validateFileType(path, ProjectFileFilterType.Topic))
    );

    // show loader until topic properties are loaded
    this.dates$.pipe(
      map(([created, modified, committed]) => created || modified || committed), // flare document must have at least one date
      tap(loaded => this.loadingState.update(!loaded)),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
